import React, { useCallback, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

interface Skill {
  id: string;
  name: string;
  value: number;
  avgValue: number;
  subSkills: {
    id: string;
    name: string;
    flag: boolean;
  }[];
}

interface Note {
  id: string;
  noteTitle: string;
  flag: boolean;
}

interface Candidate {
  id: string;
  candidateid: string | number;
  name: string;
  jobFit: string;
  score: string;
  experience: number;
  source: string;
  date: string;
  skills?: Skill[];
  notes?: Note[];
  shortlisted?: boolean;
}

interface ScorecardPDFGeneratorProps {
  candidate: Candidate;
  shortlistedCandidates: string[];
  containerRef?: React.RefObject<HTMLElement>;
  onComplete?: (success: boolean) => void;
  onError?: (error: Error) => void;
}

// Custom hook for PDF generation
export const useScorecardPDF = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const generatePDF = useCallback(
    async (
      candidate: Candidate,
      shortlistedCandidates: string[],
      containerRef?: React.RefObject<HTMLElement>
    ): Promise<void> => {
      if (!candidate) {
        setError(new Error("No candidate data provided"));
        return;
      }

      setIsGenerating(true);
      setError(null);

      // Create a new PDF document
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Set PDF metadata
      pdf.setProperties({
        title: `Candidate Panda Quiz Scorecard - ${candidate.name}`,
        subject: "Candidate Assessment",
        author: "Panda0",
        creator: "Panda0 ATS",
      });

      try {
        // Grab the scorecard container element
        const scorecardContainer =
          containerRef?.current || document.querySelector(".modal-content");
        if (!scorecardContainer) {
          throw new Error("Could not find scorecard container");
        }

        // Create a temporary clone of the element to modify for PDF capture
        const tempElement = scorecardContainer.cloneNode(true) as HTMLElement;
        tempElement.style.width = "794px"; // A4 width at 96 DPI
        tempElement.style.position = "absolute";
        tempElement.style.left = "-9999px";
        document.body.appendChild(tempElement);

        // Prepare the element for PDF capture
        // Remove videos, buttons, and other interactive elements
        const videosAndButtons = tempElement.querySelectorAll(
          "video, button, iframe"
        );
        videosAndButtons.forEach((element) => {
          if (element.tagName === "VIDEO") {
            // Replace videos with placeholder image
            const img = document.createElement("img");
            img.src =
              "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAARMAAAC3CAMAAAAGjUrGAAAAM1BMVEXMzMyTk5PPz8+SkpLLy8vQ0NCYmJjJycnS0tKVlZXe3t6ampqysrLGxsbZ2dnBwcG9vb0uX8TUAAAELUlEQVR4nO2dC3arMAxEQXwCcf+r7eMVaEnaJnEMZs5ZQVvCHST5CbTWAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAKMG6rrQ/Qwk+U9L+FPm5p2Ts5P9Bm4+Hfdau9eNh2Jod7ZyZP0MbxiFxPNr107zN43CwsBh7d0L40yLq7GTy5dPGYRX240b7I2VC+JNKi3ay+LLQtptQwjsZjjshQJC+m1C+dLHfSP+L9t2E8vWyQfvbKt9NKF8vG7S/renQTShb24bd9GirYjehjA/aBt0MH9LijUz5q/b3vTO/yE66VqZ37e98Y35V28nwrP3Nb8jPrO1kmEuImZ9bO9mqqGyVf4ydPOvOLn+Ondwm7a9/A6aFYCd2NsmLiZ/c3iEsnlMxdnJP9Y/6n2MnC7VI6V8Id6IdJfMvhDvRvux/kZ2cUyFo+Y/tZJ61T0ABZtJ9cU73Rb9ImH9p+t9A2p1oX/bPBwlwksE/+1ISBDqpCKEudEK+GwdQySG4Z0K3SwqKJ952ookXnQzapxp10e1Q2H8J+X4i/Iu2IHHzQEGdaIvFFHRCWyoI+yO0FYZwg0RbLQkfCYIdGtoCTHgOQfKEkPAogtgJbdEl3OZQ1AnlPr6gE9qCoQ2hE9oVl3CnQ9AJ6cRG+L0Tgk5oJzeC3y7CNxP7TU6oNxP7TXWrE/I1g/h7LOpOvM/g95hUnXgPzX5vnXyfR/0+nH6PR/39A/17Uvr3xvTv0enfZ9S/V6p/v/df2O8n+CwGn+fBZ4LwuSR8Ng2f0/v/nOPAuVacc9Y5d0Bw7gNZcu4HWnLuiVpy7ovbce5NXHLuD11y7pFdcu7T3nHul99x7tnfce4b4J+g36dRv1emf79Q/Z6p/n1b/XvYR/fR8dkpf4YOnyP1/2xnPmsN5BMEfJ7i73MuHD7v9efZK86znzjP4HKeBeZ5oJzn6bqeaex7rrbnGeyuZ/G7nuvgejbIv3tOwPeMl++ZQL5nRzmf4eZ6Vp/rmY6uZ38GnQXre0ax75nYvmeTu56R73pWf9CZ/SF3FhxwdkXInRVhdxadcHZJ0J0lQXeWBN1ZEnRnx/fMpqA7O77c2Y91Z8eXOzvK7rwm4DUJr5F4zcUlm5fI4TUzlyx6jcttxt7qzWvm3ubsm6h8NWdNRRxeU/XKBFxTFXf2xaC9KXwx79Lqop6dDK6vRD0Rbs97dGrhz3Nep953eAvd5HaSaO9vr3BPUPj29LrXvWmSN5Rb9oLXYZr1rZd09tG+B2JrJ0EkXesk2W+hJ6LovJ3ER5WkbdEn58HmQwuSdEjSMJVN0oF0kMvTMNdN2zIV/TTuEFmfJN0Rm6RrYpO0b9rU9ZQ+qyEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAJDlBygBQ04m93f1AAAAAElFTkSuQmCC";
            img.alt = "Video preview";
            img.style.width = "100%";
            img.style.maxWidth = "400px";
            img.style.height = "auto";
            element.parentNode?.replaceChild(img, element);
          } else {
            // Remove buttons and iframes
            element.parentNode?.removeChild(element);
          }
        });

        // Generate PDF page by page
        // Each section becomes its own page in the PDF
        const sections = tempElement.querySelectorAll("[id]");

        // Add title page
        pdf.setFontSize(24);
        pdf.text(`Candidate Scorecard`, 105, 40, { align: "center" });
        pdf.setFontSize(18);
        pdf.text(`${candidate.name}`, 105, 55, { align: "center" });
        pdf.setFontSize(12);
        pdf.text(`ID: ${candidate.candidateid}`, 105, 65, { align: "center" });
        pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, 105, 75, {
          align: "center",
        });

        // Add candidate basic info
        pdf.setFontSize(14);
        pdf.text(`Job Fit: ${candidate.jobFit}%`, 105, 95, { align: "center" });
        pdf.text(`Score: ${candidate.score}/100`, 105, 105, {
          align: "center",
        });
        pdf.text(`Experience: ${candidate.experience} years`, 105, 115, {
          align: "center",
        });
        pdf.text(`Source: ${candidate.source}`, 105, 125, { align: "center" });
        pdf.text(`Applied on: ${candidate.date}`, 105, 135, {
          align: "center",
        });

        // Status indicator
        const shortlisted =
          shortlistedCandidates.includes(candidate.id) || candidate.shortlisted;
        if (shortlisted) {
          pdf.setFillColor(0, 200, 83);
        } else {
          pdf.setFillColor(200, 0, 0);
        }

        pdf.rect(85, 150, 40, 10, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.text(shortlisted ? "SHORTLISTED" : "NOT SHORTLISTED", 105, 157, {
          align: "center",
        });
        pdf.setTextColor(0, 0, 0);

        // Add each section as a page
        let sectionIndex = 0;
        for (const section of Array.from(sections)) {
          if (sectionIndex > 0) {
            pdf.addPage();
          }

          // Section title
          const sectionId = section.id;
          const sectionTitle =
            sectionId.charAt(0).toUpperCase() + sectionId.slice(1);
          pdf.setFontSize(16);
          pdf.text(sectionTitle, 20, 20);

          try {
            // Capture section content as an image
            const canvas = await html2canvas(section as HTMLElement, {
              scale: 1.5,
              useCORS: true,
              logging: false,
              allowTaint: true,
            });

            const imgData = canvas.toDataURL("image/jpeg", 0.8);

            // Calculate dimensions to fit on the page
            const imgWidth = 170; // A4 page width in mm (210mm) minus margins
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Don't add content for the first page (it's our title page)
            if (sectionIndex > 0) {
              pdf.addImage(imgData, "JPEG", 20, 30, imgWidth, imgHeight);
            }
          } catch (err) {
            console.error(`Failed to capture section ${sectionId}:`, err);
          }

          sectionIndex++;
        }

        // Add skills section if available
        if (candidate.skills && candidate.skills.length > 0) {
          pdf.addPage();
          pdf.setFontSize(16);
          pdf.text("Technical Skills", 20, 20);

          let yPos = 40;
          candidate.skills.forEach((skill, index) => {
            if (yPos > 250) {
              pdf.addPage();
              yPos = 40;
            }

            pdf.setFontSize(14);
            pdf.text(`${skill.name}: ${skill.value}%`, 20, yPos);
            yPos += 10;

            // Draw skill progress bar
            pdf.setFillColor(220, 220, 220);
            pdf.rect(20, yPos, 160, 5, "F");

            const barColor =
              skill.value >= 75
                ? [0, 200, 83]
                : skill.value >= 50
                  ? [255, 193, 7]
                  : skill.value >= 25
                    ? [255, 152, 0]
                    : [244, 67, 54];
            pdf.setFillColor(barColor[0], barColor[1], barColor[2]);
            pdf.rect(20, yPos, skill.value * 1.6, 5, "F");
            yPos += 15;

            pdf.setFontSize(12);
            skill.subSkills.forEach((subSkill) => {
              const subSkillText = `• ${subSkill.name} ${subSkill.flag ? "✓" : "✗"}`;
              pdf.text(subSkillText, 25, yPos);
              yPos += 8;
            });

            yPos += 10;
          });
        }

        // Notes section if available
        if (candidate.notes && candidate.notes.length > 0) {
          pdf.addPage();
          pdf.setFontSize(16);
          pdf.text("Candidate Notes", 20, 20);

          let yPos = 40;
          candidate.notes.forEach((note, index) => {
            pdf.setFontSize(12);
            pdf.text(`• ${note.noteTitle}`, 25, yPos);
            yPos += 10;
          });
        }

        // Clean up the temporary element
        document.body.removeChild(tempElement);

        // Save the PDF
        pdf.save(
          `candidate_scorecard_${candidate.name.replace(/\s+/g, "_")}.pdf`
        );
        setIsGenerating(false);
      } catch (error) {
        console.error("Error generating PDF:", error);
        setError(error instanceof Error ? error : new Error("Unknown error"));

        // Fallback to a simpler PDF if the full one fails
        generateSimplePDF(candidate, pdf, shortlistedCandidates);
        setIsGenerating(false);
      }
    },
    []
  );

  // Fallback to generate a simple PDF with basic candidate info
  const generateSimplePDF = (
    candidate: Candidate,
    pdf: jsPDF,
    shortlistedCandidates: string[]
  ) => {
    pdf.setFontSize(22);
    pdf.text("Candidate Scorecard", 105, 30, { align: "center" });

    pdf.setFontSize(16);
    pdf.text(`Name: ${candidate.name}`, 20, 50);
    pdf.text(`Candidate ID: ${candidate.candidateid}`, 20, 60);
    pdf.text(`Job Fit: ${candidate.jobFit}%`, 20, 70);
    pdf.text(`Score: ${candidate.score}/100`, 20, 80);
    pdf.text(`Experience: ${candidate.experience} years`, 20, 90);
    pdf.text(`Source: ${candidate.source}`, 20, 100);
    pdf.text(`Applied on: ${candidate.date}`, 20, 110);

    const shortlisted =
      shortlistedCandidates.includes(candidate.id) || candidate.shortlisted;
    pdf.text(
      `Status: ${shortlisted ? "Shortlisted" : "Not Shortlisted"}`,
      20,
      120
    );

    if (candidate.skills && candidate.skills.length > 0) {
      pdf.text("Technical Skills:", 20, 140);

      let yPos = 150;
      candidate.skills.forEach((skill, index) => {
        pdf.setFontSize(14);
        pdf.text(`${skill.name}: ${skill.value}%`, 25, yPos);
        yPos += 10;

        pdf.setFontSize(12);
        skill.subSkills.forEach((subSkill) => {
          const status = subSkill.flag ? "Yes" : "No";
          pdf.text(`• ${subSkill.name} (${status})`, 30, yPos);
          yPos += 8;
        });

        yPos += 5;
      });
    }

    // Save the simple PDF
    pdf.save(
      `candidate_scorecard_${candidate.name.replace(/\s+/g, "_")}_simple.pdf`
    );
  };

  return { generatePDF, isGenerating, error };
};

// A React component wrapper for the PDF generator
const ScorecardPDFGenerator: React.FC<ScorecardPDFGeneratorProps> = ({
  candidate,
  shortlistedCandidates,
  containerRef,
  onComplete,
  onError,
}) => {
  const { generatePDF, isGenerating, error } = useScorecardPDF();

  const handleGeneratePDF = async () => {
    try {
      await generatePDF(candidate, shortlistedCandidates, containerRef);
      onComplete?.(true);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error("Unknown error generating PDF");
      onError?.(error);
      onComplete?.(false);
    }
  };

  return (
    <button
      onClick={handleGeneratePDF}
      style={{ display: "none" }}
      aria-hidden="true"
    />
  );
};

export default ScorecardPDFGenerator;
